import { ReactNode } from 'react';

import {
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  OutlinedInputProps,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { EndAdornmentWithStatusIcon } from '../utils/EndAdornmentWithStatusIcon';
import { FullTextTooltip } from '@/components/ui/FullTextTooltip';
import { InfoIconWithTooltip } from '@/components/ui/InfoIconWithTooltip';
import { useFieldContext } from '@/forms/formContext';
import { BaseInputFieldProps } from '@/forms/types';

interface TextareaInputFieldProps
  extends
    BaseInputFieldProps,
    Omit<
      OutlinedInputProps,
      | 'value'
      | 'onChange'
      | 'onBlur'
      | 'name'
      | 'id'
      | 'label'
      | 'required'
      | 'multiline'
    > {
  helperText?: string | ReactNode;
}

export const TextareaInputField = ({
  required,
  label,
  placeholder,
  showIsValid = false,
  infoTooltip,
  helperText,
  ...props
}: TextareaInputFieldProps) => {
  const field = useFieldContext();

  const isValid = !!(field.state.meta.isValid && field.state.meta.isDirty);
  const isError = !!(
    (!field.state.meta.isValid && field.state.meta.isDirty) ||
    (!field.state.meta.isValid && field.form.state.submissionAttempts > 0)
  );

  const { t } = useTranslation('forms');

  const infoTooltipId = infoTooltip ? `id_${field.name}_tooltip` : undefined;
  const helperTextId =
    helperText && !isError ? `id_${field.name}_helper` : undefined;
  const displayError = isError ? field.state.meta.errors[0] : null;
  const displayErrorId = displayError ? `id_${field.name}_error` : null;
  const describedByIds = [infoTooltipId, helperTextId, displayErrorId]
    .filter(Boolean)
    .join(' ');

  return (
    <FormControl valid={showIsValid && isValid} error={isError}>
      <Stack direction="row">
        <FullTextTooltip>
          <InputLabel required={required} htmlFor={`id_${field.name}`}>
            {label}
          </InputLabel>
        </FullTextTooltip>
        {infoTooltip && infoTooltipId && (
          <InfoIconWithTooltip id={infoTooltipId} tooltip={infoTooltip} />
        )}
      </Stack>
      <OutlinedInput
        {...props}
        name={field.name}
        value={field.state.value}
        placeholder={placeholder}
        minRows={props.minRows ?? 3}
        maxRows={props.maxRows ?? 8}
        multiline
        onBlur={field.handleBlur}
        onChange={(e) => field.handleChange(e.target.value)}
        inputProps={{
          id: `id_${field.name}`,
          ...(describedByIds && {
            'aria-describedby': describedByIds,
          }),
        }}
        {...(((showIsValid && isValid) || isError || props.endAdornment) && {
          endAdornment: (
            <EndAdornmentWithStatusIcon
              isValid={showIsValid && isValid}
              isError={isError}
              endAdornment={props.endAdornment}
            />
          ),
        })}
      />
      {isError && displayError && displayErrorId ? (
        <FormHelperText error id={displayErrorId}>
          {displayError.message?.startsWith('validationErrors.')
            ? t(displayError.message, {
                value: label ?? t('validationErrors.DefaultValueLabel'),
              })
            : displayError.message}
        </FormHelperText>
      ) : null}
      {helperText && !isError && (
        <FormHelperText
          id={helperTextId}
          component={typeof helperText === 'string' ? 'p' : 'div'}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
